/**
 * MessageTemplate — reusable SMS / Email messages a user composes once and edits before sending
 * (api.core, MongoDB-backed). Contract: `docs/frontend-message-templates.md`.
 *
 * ⚠️ BARE DTOs, like `CustomContact` and `Legal`: no `{ isSuccessful, data }` envelope. Read the
 * response directly. Errors are `{ code, message }` — see `messageTemplateError`.
 *
 * Facts worth knowing before editing:
 *  - The server only STORES templates. It never substitutes `{{placeholders}}`; `variables` is
 *    informational. Nothing here sends anything.
 *  - Templates belong to the USER who made them, not the party. `GET` with no `userId` lists the
 *    caller's own — which is the only list this app shows, because update/delete are owner-only.
 *  - `PUT` is a FULL REPLACE. Omit a field and it is cleared.
 *  - Email bodies are sanitised on save, so read the body back from the response rather than
 *    assuming what was sent is what was kept.
 *  - `type` other than Sms/Email on the list returns an EMPTY page, not everything.
 */

import { coreApiSlice } from '../apiSlice';

export type MessageTemplateType = 'Sms' | 'Email';

export interface IMessageTemplate {
	id: string;
	ownerUserId: number;
	name: string;
	type: MessageTemplateType;
	/** Email only. Always null for Sms. */
	subject: string | null;
	body: string;
	/** Every `{{name}}` in subject + body, in document order, de-duplicated. Derived server-side. */
	variables: string[];
	createdAt: string;
	updatedAt: string;
}

export interface IMessageTemplateInput {
	name: string;
	type: MessageTemplateType;
	subject: string | null;
	body: string;
}

export interface IMessageTemplateListParams {
	type?: MessageTemplateType;
	search?: string;
	pageNumber?: number;
	/** 1–100; anything else silently falls back to 20. */
	pageSize?: number;
}

export interface IMessageTemplatePage {
	items: IMessageTemplate[];
	pageNumber: number;
	pageSize: number;
	total: number;
}

const BASE = '/MessageTemplate';

// Tag types added here rather than on coreApiSlice, for the same zero-blast-radius reason as
// custom-contact.api.ts.
const templateApi = coreApiSlice.enhanceEndpoints({ addTagTypes: ['MessageTemplate'] });

export const messageTemplateApiSlice = templateApi.injectEndpoints({
	endpoints: (builder) => ({
		getMessageTemplates: builder.query<IMessageTemplatePage, IMessageTemplateListParams>({
			query: ({ type, search, pageNumber = 1, pageSize = 20 }) => ({
				url: BASE,
				params: {
					pageNumber,
					pageSize,
					...(type ? { type } : {}),
					...(search?.trim() ? { search: search.trim() } : {}),
				},
			}),
			providesTags: (result) => [
				{ type: 'MessageTemplate', id: 'LIST' },
				...(result?.items ?? []).map((t) => ({ type: 'MessageTemplate' as const, id: t.id })),
			],
		}),
		createMessageTemplate: builder.mutation<IMessageTemplate, IMessageTemplateInput>({
			query: (body) => ({ url: BASE, method: 'POST', body }),
			invalidatesTags: [{ type: 'MessageTemplate', id: 'LIST' }],
		}),
		updateMessageTemplate: builder.mutation<IMessageTemplate, { id: string; body: IMessageTemplateInput }>({
			query: ({ id, body }) => ({ url: `${BASE}/${id}`, method: 'PUT', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'MessageTemplate', id },
				{ type: 'MessageTemplate', id: 'LIST' },
			],
		}),
		deleteMessageTemplate: builder.mutation<undefined, string>({
			query: (id) => ({ url: `${BASE}/${id}`, method: 'DELETE' }),
			invalidatesTags: (_r, _e, id) => [
				{ type: 'MessageTemplate', id },
				{ type: 'MessageTemplate', id: 'LIST' },
			],
		}),
	}),
});

export const {
	useGetMessageTemplatesQuery,
	useCreateMessageTemplateMutation,
	useUpdateMessageTemplateMutation,
	useDeleteMessageTemplateMutation,
} = messageTemplateApiSlice;

// ── Errors ───────────────────────────────────────────────────

export type MessageTemplateField = 'name' | 'subject' | 'body' | 'form';

export interface IMessageTemplateError {
	/** Which field the message belongs beside. `form` when it belongs to no single field. */
	field: MessageTemplateField;
	code: string | null;
	message: string;
}

const FIELD_BY_CODE: Record<string, MessageTemplateField> = {
	INVALID_NAME: 'name',
	NAME_TAKEN: 'name',
	SUBJECT_REQUIRED: 'subject',
	INVALID_BODY: 'body',
	BODY_TOO_LONG: 'body',
	SMS_HTML_NOT_ALLOWED: 'body',
	INVALID_TYPE: 'form',
};

// Our own words for the codes an operator can act on. The server's `message` is the fallback.
const MESSAGE_BY_CODE: Record<string, string> = {
	NAME_TAKEN: 'You already have a template with this name.',
	SUBJECT_REQUIRED: 'An email template needs a subject.',
	SMS_HTML_NOT_ALLOWED: 'SMS templates are plain text — remove anything that looks like an HTML tag (e.g. <b>).',
	BODY_TOO_LONG: 'This message is too long to save (100,000 characters at most).',
	INVALID_BODY: 'Write a message before saving.',
	INVALID_NAME: 'Give the template a name (200 characters at most).',
};

/**
 * Map an RTK Query rejection onto the field it concerns.
 *
 * <p>`err.data` is the parsed body, and this controller's body IS `{ code, message }` — no envelope
 * around it. A 403 carries no code: it means the template belongs to someone else.</p>
 */
export const messageTemplateError = (err: unknown): IMessageTemplateError => {
	const e = (err ?? {}) as { status?: number | string; data?: { code?: string; message?: string } | string };
	const body = typeof e.data === 'object' && e.data ? e.data : null;
	const code = body?.code ?? null;
	if (code) {
		return {
			field: FIELD_BY_CODE[code] ?? 'form',
			code,
			message: MESSAGE_BY_CODE[code] ?? body?.message ?? 'This template could not be saved.',
		};
	}
	if (e.status === 403) return { field: 'form', code: null, message: 'Only the person who created this template can change it.' };
	if (e.status === 404) return { field: 'form', code: null, message: 'This template no longer exists.' };
	return { field: 'form', code: null, message: body?.message ?? 'Something went wrong. Try again.' };
};
